import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { IconTile } from '@atlaskit/icon';
import AttachmentIcon from '@atlaskit/icon/core/attachment';
import AudioIcon from '@atlaskit/icon/core/audio';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ImageIcon from '@atlaskit/icon/core/image';
import PageIcon from '@atlaskit/icon/core/page';
import VideoIcon from '@atlaskit/icon/core/video';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import type { ExternalAttachment, SearchResultMessagePartial } from '@atlassian/search-client';

import { LastModified } from '../../../../../../../../common/ui/last-modified';

import { i18n } from './messages';

const footerContainer = xcss({
	paddingTop: 'space.100',
	position: 'relative',
});

const dropdownItemStyles = xcss({
	display: 'flex',
	columnGap: 'space.100',
	paddingBlock: 'space.050',
	marginTop: 'space.100',
});

const attachmentStyles = xcss({
	color: 'color.link',
	textDecoration: 'none',

	':hover': {
		color: 'color.link',
		textDecoration: 'underline',
	},
	':active': {
		textDecoration: 'none',
	},
	':visited': {
		color: 'color.link.visited',
	},
	':visited:active': {
		color: 'color.link.visited.pressed',
	},
});

const mimeTypeToIcon = (mimeType: string | undefined) => {
	if (!mimeType) {
		return AttachmentIcon;
	}

	const [type, specificType] = mimeType.split('/');
	if (specificType === 'pdf') {
		return PageIcon;
	}

	switch (type) {
		case 'video':
			return VideoIcon;
		case 'image':
			return ImageIcon;
		case 'audio':
			return AudioIcon;
		default:
			return AttachmentIcon;
	}
};

const mimeTypeToLabel = (mimeType: string | undefined) => {
	if (!mimeType) {
		return i18n.attachmentLabel;
	}

	const [type, specificType] = mimeType.split('/');
	if (specificType === 'pdf') {
		return i18n.pdfLabel;
	}

	switch (type) {
		case 'video':
			return i18n.videoLabel;
		case 'image':
			return i18n.imageLabel;
		case 'audio':
			return i18n.audioLabel;
		default:
			return i18n.attachmentLabel;
	}
};

export const DropdownItems = ({
	attachments,
	message,
}: {
	attachments: ExternalAttachment[];
	message: SearchResultMessagePartial;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box>
			{attachments.map(({ title, mimeType, url, thumbnailUrl }) => {
				const truncatedUrl = url?.replace(/\/download\//g, '/');
				const viewUrl = mimeType?.includes('image') ? truncatedUrl : thumbnailUrl;

				return (
					<Stack xcss={dropdownItemStyles} space="space.050">
						<Inline alignBlock="center" space="space.025">
							<IconTile size="16" icon={mimeTypeToIcon(mimeType)} label="icon" appearance="blue" />
							<Text size="medium" color="color.link" weight="medium">
								{viewUrl ? (
									<Anchor xcss={attachmentStyles} href={viewUrl} rel={'noreferrer'} target="_blank">
										{title}
									</Anchor>
								) : (
									title
								)}
							</Text>
						</Inline>
						<Inline separator={<Text size="small">•</Text>} space="space.050" alignBlock="center">
							<Text size="small">{formatMessage(mimeTypeToLabel(mimeType))}</Text>
							<Text size="small" color="color.text.subtle">
								<LastModified
									lastModified={message.lastModifiedDate}
									message={i18n.attachedDateLabel}
								/>
							</Text>
						</Inline>
					</Stack>
				);
			})}
		</Box>
	);
};

const DropdownMenu = ({
	attachments,
	message,
}: {
	attachments: ExternalAttachment[];
	message: SearchResultMessagePartial;
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const { formatMessage } = useIntl();

	const attachmentWord = `attachment${attachments.length > 1 ? 's' : ''}`;
	const dropdownMessage = `${attachments.length} ${attachmentWord}`;

	return (
		<Box>
			<Button
				iconAfter={
					isExpanded
						? () => (
								<ChevronDownIcon
									color="currentColor"
									label={formatMessage(i18n.expandLabel)}
									size="small"
								/>
							)
						: () => (
								<ChevronRightIcon
									color="currentColor"
									label={formatMessage(i18n.expandLabel)}
									size="small"
								/>
							)
				}
				onClick={() => setIsExpanded(!isExpanded)}
				isSelected={isExpanded}
			>
				{dropdownMessage}
			</Button>
			{isExpanded && <DropdownItems attachments={attachments} message={message} />}
		</Box>
	);
};

export const Attachments = ({
	attachments,
	message,
}: {
	attachments?: ExternalAttachment[];
	message: SearchResultMessagePartial;
}): React.JSX.Element | null => {
	if (!attachments || attachments.length === 0) {
		return null;
	}

	return (
		<Box xcss={footerContainer}>
			<DropdownMenu attachments={attachments} message={message}></DropdownMenu>
		</Box>
	);
};
